/**
 * AdaptiveClearing - Adaptive Toolpath Generation with Trochoidal Motion
 *
 * Advanced clearing strategy implementing:
 * - Medial Axis Transform (MAT) for optimal path
 * - Constant Tool Engagement Angle control
 * - Trochoidal arcs for high radial engagement
 * - Dynamic feedrate optimization
 *
 * Benefits:
 * - Consistent chip load → longer tool life
 * - Higher MRR (Material Removal Rate) in tight areas
 * - Reduced cutting forces and vibration
 * - Better chip evacuation
 *
 * Based on Volumill, HSM Works, and Fusion 360 Adaptive algorithms
 *
 * @author FikoYT CAM System
 * @version 1.0.0
 */

import { Vector2 } from 'three';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Adaptive Clearing Parametreleri
 */
export interface AdaptiveParams {
    /**
     * Optimal Load - Radial angajman oranı
     * Takım çapının hangi yüzdesi malzemeye temas edecek
     *
     * Tipik: 0.10 - 0.25 (%10 - %25 radial engagement)
     * - Düşük değer (0.10): Daha yumuşak, daha uzun yol
     * - Yüksek değer (0.25): Daha agresif, daha kısa yol
     */
    optimalLoad: number;

    /**
     * Tool Diameter - Takım çapı (mm)
     */
    toolDiameter: number;

    /**
     * Stepover - Maksimum yanal hatve
     * Açık bölgelerde kullanılacak maksimum step
     *
     * Tipik: %40-60 takım çapı
     */
    stepover: number;

    /**
     * Stock to Leave - Duvar payı (mm)
     * Kenar duvarlarda bırakılacak finish payı
     */
    stockToLeave: number;

    /**
     * Trochoidal Mode - Trochoidal hareket aktif mi
     * true: Dar bölgelerde trochoidal arc kullan
     * false: Sadece linear zigzag
     */
    trochoidalMode: boolean;

    /**
     * Trochoidal Diameter - Trochoidal arc çapı (mm)
     * Dar geçitlerde kullanılacak trochoidal hareket çapı
     *
     * Tipik: %40-80 takım çapı
     */
    trochoidalDiameter?: number;

    /**
     * Engagement Angle Limit - Maksimum angajman açısı (derece)
     * Bu açıyı aşarsa trochoidal hareket devreye girer
     *
     * Tipik: 120-180°
     */
    engagementAngleLimit?: number;

    /**
     * Minimum Tool Engagement - Minimum takım angajmanı
     * Bu değerin altına düşerse yol atlanır (optimization)
     *
     * Tipik: %5 takım çapı
     */
    minToolEngagement?: number;
}

/**
 * Toolpath Segment - Yol parçası
 */
export interface ToolpathSegment {
    type: 'line' | 'arc' | 'trochoidal';
    start: Vector2;
    end: Vector2;

    // Arc için
    center?: Vector2;
    radius?: number;
    clockwise?: boolean;
    sweepAngle?: number;

    // Metadata
    engagement?: number;  // Radial engagement (mm)
    feedrate?: number;    // Önerilen feedrate (mm/min)
}

/**
 * Medial Axis Segment - Medial eksen parçası
 */
interface MedialAxisSegment {
    start: Vector2;
    end: Vector2;
    clearance: number;  // En yakın duvara mesafe
}

// ═══════════════════════════════════════════════════════════════════════════
// ADAPTIVE CLEARING GENERATOR
// ═══════════════════════════════════════════════════════════════════════════

export class AdaptiveClearing {
    private params: AdaptiveParams;
    private boundary: Vector2[];
    private islands: Vector2[][];
    private medialAxis: MedialAxisSegment[] = [];

    constructor(
        boundary: Vector2[],
        params: AdaptiveParams,
        islands: Vector2[][] = []
    ) {
        this.boundary = boundary;
        this.params = this.initializeParams(params);
        this.islands = islands;
    }

    /**
     * Parametreleri başlat ve varsayılanları ekle
     */
    private initializeParams(params: AdaptiveParams): AdaptiveParams {
        return {
            ...params,
            trochoidalDiameter: params.trochoidalDiameter || params.toolDiameter * 0.6,
            engagementAngleLimit: params.engagementAngleLimit || 150,
            minToolEngagement: params.minToolEngagement || params.toolDiameter * 0.05
        };
    }

    /**
     * Ana toolpath oluşturma metodu
     */
    public generateToolpath(): ToolpathSegment[] {
        const toolpath: ToolpathSegment[] = [];

        try {
            // 1. Medial axis hesapla
            this.computeMedialAxis();

            // 2. Medial axis boyunca zigzag yollar oluştur
            const zigzagPaths = this.generateZigzagAlongMedial();

            // 3. Her segment için engagement kontrolü ve trochoidal ekleme
            for (const path of zigzagPaths) {
                const processedSegments = this.processPathSegment(path);
                toolpath.push(...processedSegments);
            }

            // 4. Boundary finishing pass
            if (this.params.stockToLeave > 0) {
                const finishPass = this.generateFinishPass();
                toolpath.push(...finishPass);
            }

        } catch (error) {
            console.error('[AdaptiveClearing] Toolpath generation error:', error);
            // Fallback: Simple offset strategy
            return this.generateFallbackToolpath();
        }

        return toolpath;
    }

    // ═══════════════════════════════════════════════════════════════════
    // MEDIAL AXIS TRANSFORM
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Medial Axis Transform (MAT) hesaplama
     * Simplified Voronoi-based approach
     */
    private computeMedialAxis(): void {
        this.medialAxis = [];

        // Basitleştirilmiş medial axis: boundary'nin offsetli versiyonları
        // Gerçek MAT için Voronoi diagram gerekir (ağır hesaplama)

        const offsetDistance = this.params.toolDiameter / 2 + this.params.stockToLeave;
        const maxOffset = this.calculateMaxPocketWidth() / 2;

        // Merkeze doğru offsetler oluştur
        let currentOffset = offsetDistance;
        const offsetStep = this.params.stepover;

        while (currentOffset < maxOffset) {
            const offsetBoundary = this.offsetPolygon(this.boundary, -currentOffset);

            if (offsetBoundary.length < 3) break;  // Polygon çökmüş

            // Offset boundary'yi segment'lere böl
            for (let i = 0; i < offsetBoundary.length; i++) {
                const start = offsetBoundary[i];
                const end = offsetBoundary[(i + 1) % offsetBoundary.length];
                const clearance = this.calculateClearance(start);

                this.medialAxis.push({
                    start: start.clone(),
                    end: end.clone(),
                    clearance
                });
            }

            currentOffset += offsetStep;
        }
    }

    /**
     * Maksimum pocket genişliğini hesapla (bounding box)
     */
    private calculateMaxPocketWidth(): number {
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        for (const pt of this.boundary) {
            minX = Math.min(minX, pt.x);
            maxX = Math.max(maxX, pt.x);
            minY = Math.min(minY, pt.y);
            maxY = Math.max(maxY, pt.y);
        }

        return Math.max(maxX - minX, maxY - minY);
    }

    /**
     * Noktanın en yakın duvara clearance'ı
     */
    private calculateClearance(point: Vector2): number {
        let minDist = Infinity;

        // Boundary'ye mesafe
        for (let i = 0; i < this.boundary.length; i++) {
            const p1 = this.boundary[i];
            const p2 = this.boundary[(i + 1) % this.boundary.length];
            const dist = this.pointToSegmentDistance(point, p1, p2);
            minDist = Math.min(minDist, dist);
        }

        // Island'lara mesafe
        for (const island of this.islands) {
            for (let i = 0; i < island.length; i++) {
                const p1 = island[i];
                const p2 = island[(i + 1) % island.length];
                const dist = this.pointToSegmentDistance(point, p1, p2);
                minDist = Math.min(minDist, dist);
            }
        }

        return minDist;
    }

    // ═══════════════════════════════════════════════════════════════════
    // ZIGZAG PATH GENERATION
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Medial axis boyunca zigzag yollar oluştur
     */
    private generateZigzagAlongMedial(): Vector2[][] {
        const paths: Vector2[][] = [];

        if (this.medialAxis.length === 0) {
            return paths;
        }

        // Medial axis segment'lerini bağla
        const sortedSegments = this.sortMedialSegments();

        for (const segment of sortedSegments) {
            const clearance = segment.clearance;
            const toolRadius = this.params.toolDiameter / 2;

            // Clearance yeterli mi?
            if (clearance < toolRadius + this.params.stockToLeave) {
                continue;  // Takım sığmaz
            }

            // Segment boyunca zigzag oluştur
            const zigzag = this.createZigzagForSegment(segment);
            if (zigzag.length > 0) {
                paths.push(zigzag);
            }
        }

        return paths;
    }

    /**
     * Medial segment'leri sırala (bağlantılı yol için)
     */
    private sortMedialSegments(): MedialAxisSegment[] {
        if (this.medialAxis.length === 0) return [];

        const sorted: MedialAxisSegment[] = [this.medialAxis[0]];
        const remaining = this.medialAxis.slice(1);

        while (remaining.length > 0) {
            const last = sorted[sorted.length - 1];
            let closestIdx = 0;
            let closestDist = Infinity;

            // En yakın segment'i bul
            for (let i = 0; i < remaining.length; i++) {
                const dist = last.end.distanceTo(remaining[i].start);
                if (dist < closestDist) {
                    closestDist = dist;
                    closestIdx = i;
                }
            }

            sorted.push(remaining[closestIdx]);
            remaining.splice(closestIdx, 1);
        }

        return sorted;
    }

    /**
     * Bir segment için zigzag yolu oluştur
     */
    private createZigzagForSegment(segment: MedialAxisSegment): Vector2[] {
        const points: Vector2[] = [];

        // Segment yönü ve normali
        const direction = new Vector2().subVectors(segment.end, segment.start).normalize();
        const normal = new Vector2(-direction.y, direction.x);

        // Zigzag genişliği
        const halfWidth = Math.min(
            segment.clearance - this.params.toolDiameter / 2 - this.params.stockToLeave,
            this.params.stepover / 2
        );

        if (halfWidth < this.params.minToolEngagement!) {
            return points;  // Çok dar
        }

        // Segment boyunca zigzag noktaları
        const segmentLength = segment.start.distanceTo(segment.end);
        const stepCount = Math.max(2, Math.ceil(segmentLength / this.params.stepover));
        const step = segmentLength / stepCount;

        for (let i = 0; i <= stepCount; i++) {
            const t = i / stepCount;
            const center = new Vector2().lerpVectors(segment.start, segment.end, t);

            // Zigzag: alternatif yan
            const side = (i % 2 === 0) ? 1 : -1;
            const offset = normal.clone().multiplyScalar(side * halfWidth);
            const point = center.clone().add(offset);

            points.push(point);
        }

        return points;
    }

    // ═══════════════════════════════════════════════════════════════════
    // TROCHOIDAL MOTION
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Path segment'i işle: Engagement kontrolü ve trochoidal ekleme
     */
    private processPathSegment(path: Vector2[]): ToolpathSegment[] {
        const segments: ToolpathSegment[] = [];

        for (let i = 0; i < path.length - 1; i++) {
            const start = path[i];
            const end = path[i + 1];

            // Engagement hesapla
            const engagement = this.calculateEngagement(start, end);

            // Engagement kontrolü
            if (engagement.radialEngagement > this.params.optimalLoad * this.params.toolDiameter) {
                // Çok yüksek engagement - trochoidal ekle
                if (this.params.trochoidalMode) {
                    const trochoidalSegs = this.generateTrochoidalMotion(start, end, engagement);
                    segments.push(...trochoidalSegs);
                } else {
                    // Trochoidal kapalı - doğrudan git ama yavaş feedrate
                    segments.push({
                        type: 'line',
                        start: start.clone(),
                        end: end.clone(),
                        engagement: engagement.radialEngagement,
                        feedrate: this.calculateFeedrate(engagement.radialEngagement, true)
                    });
                }
            } else {
                // Normal engagement - doğrudan git
                segments.push({
                    type: 'line',
                    start: start.clone(),
                    end: end.clone(),
                    engagement: engagement.radialEngagement,
                    feedrate: this.calculateFeedrate(engagement.radialEngagement, false)
                });
            }
        }

        return segments;
    }

    /**
     * İki nokta arasında radial engagement hesapla
     */
    private calculateEngagement(start: Vector2, end: Vector2): {
        radialEngagement: number;
        engagementAngle: number;
    } {
        const direction = new Vector2().subVectors(end, start).normalize();
        const midPoint = new Vector2().addVectors(start, end).multiplyScalar(0.5);

        // En yakın duvarlar
        const leftDist = this.calculateClearance(midPoint);
        const normal = new Vector2(-direction.y, direction.x);
        const rightPoint = midPoint.clone().add(normal.clone().multiplyScalar(this.params.toolDiameter));
        const rightDist = this.calculateClearance(rightPoint);

        // Radial engagement = takımın malzemeye temas eden kısmı
        const radialEngagement = Math.min(leftDist, rightDist);

        // Engagement angle (simplified)
        const engagementAngle = (radialEngagement / this.params.toolDiameter) * 180;

        return {
            radialEngagement,
            engagementAngle
        };
    }

    /**
     * Trochoidal hareket oluştur (circular arcs)
     */
    private generateTrochoidalMotion(
        start: Vector2,
        end: Vector2,
        engagement: { radialEngagement: number; engagementAngle: number }
    ): ToolpathSegment[] {
        const segments: ToolpathSegment[] = [];

        const totalDist = start.distanceTo(end);
        const trochRadius = this.params.trochoidalDiameter! / 2;
        const loopCount = Math.ceil(totalDist / (trochRadius * 2));

        const direction = new Vector2().subVectors(end, start).normalize();
        const normal = new Vector2(-direction.y, direction.x);

        for (let i = 0; i < loopCount; i++) {
            const t = i / loopCount;
            const loopCenter = new Vector2().lerpVectors(start, end, t);

            // Circular loop (trochoidal arc)
            const loopStart = loopCenter.clone().add(normal.clone().multiplyScalar(trochRadius));
            const loopEnd = loopCenter.clone().add(normal.clone().multiplyScalar(-trochRadius));

            // Arc segment (semi-circle)
            segments.push({
                type: 'trochoidal',
                start: loopStart.clone(),
                end: loopEnd.clone(),
                center: loopCenter.clone(),
                radius: trochRadius,
                clockwise: true,
                sweepAngle: 180,
                engagement: this.params.optimalLoad * this.params.toolDiameter,
                feedrate: this.calculateFeedrate(this.params.optimalLoad * this.params.toolDiameter, false)
            });

            // Return arc
            segments.push({
                type: 'trochoidal',
                start: loopEnd.clone(),
                end: loopCenter.clone().add(direction.clone().multiplyScalar(trochRadius * 2)),
                center: loopCenter.clone(),
                radius: trochRadius,
                clockwise: true,
                sweepAngle: 180,
                engagement: this.params.optimalLoad * this.params.toolDiameter,
                feedrate: this.calculateFeedrate(this.params.optimalLoad * this.params.toolDiameter, false)
            });
        }

        return segments;
    }

    /**
     * Engagement'e göre feedrate hesapla
     */
    private calculateFeedrate(radialEngagement: number, highEngagement: boolean): number {
        const optimalEngagement = this.params.optimalLoad * this.params.toolDiameter;
        const engagementRatio = radialEngagement / optimalEngagement;

        // Feedrate inversely proportional to engagement
        // Daha fazla engagement = daha düşük feedrate
        let feedrateMultiplier = 1.0;

        if (highEngagement) {
            feedrateMultiplier = 0.5;  // %50 azalt
        } else if (engagementRatio > 1.5) {
            feedrateMultiplier = 0.7;
        } else if (engagementRatio < 0.5) {
            feedrateMultiplier = 1.3;  // Az engagement - hızlan
        }

        return feedrateMultiplier;  // Multiplier döner (caller tarafından uygulanır)
    }

    // ═══════════════════════════════════════════════════════════════════
    // FINISH PASS
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Final finish pass oluştur (boundary offset)
     */
    private generateFinishPass(): ToolpathSegment[] {
        const segments: ToolpathSegment[] = [];

        const finishOffset = -(this.params.toolDiameter / 2);
        const finishBoundary = this.offsetPolygon(this.boundary, finishOffset);

        for (let i = 0; i < finishBoundary.length; i++) {
            const start = finishBoundary[i];
            const end = finishBoundary[(i + 1) % finishBoundary.length];

            segments.push({
                type: 'line',
                start: start.clone(),
                end: end.clone(),
                engagement: this.params.toolDiameter * 0.1,  // Hafif temas
                feedrate: 1.0  // Normal feedrate
            });
        }

        return segments;
    }

    // ═══════════════════════════════════════════════════════════════════
    // FALLBACK STRATEGY
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Fallback: Basit offset strategy (adaptive başarısız olursa)
     */
    private generateFallbackToolpath(): ToolpathSegment[] {
        const segments: ToolpathSegment[] = [];

        const offset = -(this.params.toolDiameter / 2 + this.params.stockToLeave);
        let currentBoundary = this.offsetPolygon(this.boundary, offset);

        while (currentBoundary.length >= 3) {
            for (let i = 0; i < currentBoundary.length; i++) {
                const start = currentBoundary[i];
                const end = currentBoundary[(i + 1) % currentBoundary.length];

                segments.push({
                    type: 'line',
                    start: start.clone(),
                    end: end.clone(),
                    engagement: this.params.stepover,
                    feedrate: 1.0
                });
            }

            currentBoundary = this.offsetPolygon(currentBoundary, -this.params.stepover);
        }

        return segments;
    }

    // ═══════════════════════════════════════════════════════════════════
    // GEOMETRY UTILITIES
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Polygon offset (Clipper library benzeri basit yaklaşım)
     */
    private offsetPolygon(polygon: Vector2[], offset: number): Vector2[] {
        // Simplified offset - gerçek uygulamada Clipper.js kullanılmalı
        // Winding-bağımsız: negatif offset = İÇE, pozitif = DIŞA (CCW/CW fark etmez).
        // (-v.y, v.x) sol normal CCW'de içe bakar; bu yüzden CCW'de yönü ters çevir.
        const offsetPoly: Vector2[] = [];

        let alan2 = 0;
        for (let i = 0; i < polygon.length; i++) {
            const a = polygon[i], b = polygon[(i + 1) % polygon.length];
            alan2 += a.x * b.y - b.x * a.y;
        }
        const yon = alan2 >= 0 ? -1 : 1; // CCW (alan>0): sol normal içe → dışa çevir

        for (let i = 0; i < polygon.length; i++) {
            const prev = polygon[(i - 1 + polygon.length) % polygon.length];
            const curr = polygon[i];
            const next = polygon[(i + 1) % polygon.length];

            // Ortalama normal
            const v1 = new Vector2().subVectors(curr, prev).normalize();
            const v2 = new Vector2().subVectors(next, curr).normalize();
            const normal1 = new Vector2(-v1.y, v1.x);
            const normal2 = new Vector2(-v2.y, v2.x);
            const avgNormal = new Vector2().addVectors(normal1, normal2).normalize();

            const offsetPoint = curr.clone().add(avgNormal.multiplyScalar(offset * yon));
            offsetPoly.push(offsetPoint);
        }

        return offsetPoly;
    }

    /**
     * Nokta-segment mesafesi
     */
    private pointToSegmentDistance(point: Vector2, segStart: Vector2, segEnd: Vector2): number {
        const seg = new Vector2().subVectors(segEnd, segStart);
        const segLenSq = seg.lengthSq();

        if (segLenSq === 0) {
            return point.distanceTo(segStart);
        }

        const t = Math.max(0, Math.min(1, new Vector2().subVectors(point, segStart).dot(seg) / segLenSq));
        const projection = segStart.clone().add(seg.multiplyScalar(t));

        return point.distanceTo(projection);
    }
}

/**
 * Utility function: Adaptive toolpath oluştur
 */
export function generateAdaptiveToolpath(
    boundary: Vector2[],
    params: AdaptiveParams,
    islands: Vector2[][] = []
): ToolpathSegment[] {
    const generator = new AdaptiveClearing(boundary, params, islands);
    return generator.generateToolpath();
}
